/**
 * The few formatting moves every joke shares, so the catalogue never invents
 * its own arithmetic. Numbers follow the app's conventions: decimal comma,
 * U+2212 for a real minus, integers for kilocalories.
 */

import { NAMED_LINE_MAX_CHARS, pluralLt, type SassContext, vocativeLt } from '@gramas/shared'

/** A whole-number kcal amount. Negative values get the true minus sign. */
export function fmtKcal(value: number | null | undefined): string {
  const v = Math.round(value ?? 0)
  return v < 0 ? `−${Math.abs(v)}` : `${v}`
}

/** One decimal, comma-separated — the weight-trend format. */
export function fmtKg(value: number | null | undefined): string {
  const v = value ?? 0
  const body = Math.abs(v).toFixed(1).replace('.', ',')
  return v < 0 ? `−${body}` : body
}

/** How far over budget the day is, as a positive figure. */
export function overBy(ctx: SassContext): string {
  const over = ctx.overByKcal ?? (ctx.remainingKcal != null ? -ctx.remainingKcal : 0)
  return fmtKcal(Math.max(0, over))
}

/** ", Eimantai" when there is a name to say, nothing when there is not. */
export function voc(ctx: SassContext): string {
  const name = ctx.name?.trim()
  return name ? `, ${vocativeLt(name)}` : ''
}

/** The meal slot in lowercase running text; the label maps stay in the repo. */
const SLOT_WORD: Readonly<Record<string, string>> = {
  breakfast: 'pusryčiai',
  lunch: 'pietūs',
  dinner: 'vakarienė',
  snack: 'užkandžiai',
}

export function slotWord(ctx: SassContext): string {
  return SLOT_WORD[ctx.slot ?? ''] ?? 'valgis'
}

/** The slot in the accusative, for "perkelta į …". */
const SLOT_ACC: Readonly<Record<string, string>> = {
  breakfast: 'pusryčius',
  lunch: 'pietus',
  dinner: 'vakarienę',
  snack: 'užkandžius',
}

export function slotAcc(ctx: SassContext): string {
  return SLOT_ACC[ctx.slot ?? ''] ?? 'kitą valgį'
}

/** Which snack this commit was: the count before it, plus this one. */
export function snackNo(ctx: SassContext): number {
  return (ctx.snackCountToday ?? 0) + 1
}

/** "4 užkandžiai" / "13 užkandžių" — this snack included, agreed with its count. */
export function snackCountLt(ctx: SassContext): string {
  const n = snackNo(ctx)
  return `${n} ${pluralLt(n, 'užkandis', 'užkandžiai', 'užkandžių')}`
}

/** "4 kartai" — how many times this same thing has been logged this week. */
export function repeatTimes(ctx: SassContext): string {
  const n = ctx.repeatCountWeek ?? 0
  return `${n} ${pluralLt(n, 'kartas', 'kartai', 'kartų')}`
}

// Only the counts `REPEAT_FOOD_NOTABLE` can produce ever reach these, but the
// numeric fallback stays in so raising that constant is a one-line change
// rather than a copy bug that ships as "5-as kebabas".
const ORDINAL_NOM: Readonly<Record<number, string>> = {
  4: 'Ketvirtas',
  5: 'Penktas',
  6: 'Šeštas',
  7: 'Septintas',
}
const ORDINAL_ACC: Readonly<Record<number, string>> = {
  4: 'ketvirtą',
  5: 'penktą',
  6: 'šeštą',
  7: 'septintą',
}

/** "Ketvirtas" — capitalised, for a line that opens on the ordinal. */
export function repeatNth(ctx: SassContext): string {
  const n = ctx.repeatCountWeek ?? 0
  return ORDINAL_NOM[n] ?? `${n}-as`
}

/** "ketvirtą" — for "… ketvirtą kartą per savaitę". */
export function repeatNthAcc(ctx: SassContext): string {
  const n = ctx.repeatCountWeek ?? 0
  return ORDINAL_ACC[n] ?? `${n}-ą`
}

/**
 * The entry name a delete line repeats back; never empty — the undo button
 * next to it has to be about something — and capped so a catalogue-length
 * product name cannot push the joke past the toast's two lines.
 */
export function itemName(ctx: SassContext): string {
  const name = ctx.itemName?.trim()
  if (!name) return 'įrašas'
  return name.length > 40 ? `${name.slice(0, 39).trimEnd()}…` : name
}

/**
 * The food's name for a line built *around* it rather than merely repeating
 * it back. Capped much harder than `itemName`: the call site only sets
 * `namedOk` for names within `NAMED_LINE_MAX_CHARS`, but the catalogue lint
 * renders every pool at maximal context, and a named joke has to survive a
 * fifty-character supermarket product name without eating the sentence.
 */
export function shortName(ctx: SassContext): string {
  const name = ctx.itemName?.trim()
  if (!name) return 'tai'
  return name.length > NAMED_LINE_MAX_CHARS
    ? `${name.slice(0, NAMED_LINE_MAX_CHARS - 1).trimEnd()}…`
    : name
}

/** The same, lowercased for mid-sentence use — product names arrive capitalised. */
export function shortNameLower(ctx: SassContext): string {
  const name = shortName(ctx)
  return name.charAt(0).toLocaleLowerCase('lt') + name.slice(1)
}

/**
 * The promise being quoted back, capped hard: the line also has to carry the
 * date and the thing that broke it, and a promise is quotable or it isn't.
 */
export function promiseQuote(ctx: SassContext): string {
  const text = ctx.promiseText?.trim()
  if (!text) return 'pažadas'
  return text.length > 32 ? `${text.slice(0, 31).trimEnd()}…` : text
}

const MONTH_GEN = [
  'sausio',
  'vasario',
  'kovo',
  'balandžio',
  'gegužės',
  'birželio',
  'liepos',
  'rugpjūčio',
  'rugsėjo',
  'spalio',
  'lapkričio',
  'gruodžio',
]

/** "rugpjūčio 20 d." from an ISO date — the receipts a broken promise carries. */
export function promiseDay(ctx: SassContext): string {
  const iso = ctx.promiseDate
  if (iso == null) return 'aną dieną'
  const month = Number(iso.slice(5, 7))
  const day = Number(iso.slice(8, 10))
  const name = MONTH_GEN[month - 1]
  if (name == null || Number.isNaN(day)) return 'aną dieną'
  return `${name} ${day} d.`
}
